import React, { useEffect, useRef, useState } from 'react';
import { originalConsole } from '../../util/setupConsole';

// Google reCAPTCHA Enterprise with a score-based (invisible) key.
// Loads enterprise.js for the site key and runs one execute() when mounted;
// the token is passed to onVerify. A score-based key never shows a checkbox.
// Every failure is logged to the console with a "[reCAPTCHA]" prefix, even
// when app logging is switched off (see util/setupConsole.js).

const SCRIPT_ID = 'recaptcha-enterprise-script';
const BADGE_STYLE_ID = 'recaptcha-enterprise-badge';
const TIMEOUT_MS = 15000;
const LOG = '[reCAPTCHA]';
const logError = (...args) => (originalConsole.error || console.error)(...args);

// The floating badge would sit on top of the chatbot button. Hiding it is
// allowed while the "Protected by reCAPTCHA" text and links stay visible.
const hideBadge = () => {
  if (document.getElementById(BADGE_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = BADGE_STYLE_ID;
  style.textContent = '.grecaptcha-badge { visibility: hidden !important; }';
  document.head.appendChild(style);
};

const isReady = () => !!(window.grecaptcha && window.grecaptcha.enterprise && window.grecaptcha.enterprise.ready);

// Resolves once grecaptcha.enterprise is usable; rejects if the script fails
// to load or does not initialise in time.
const loadEnterprise = (siteKey) =>
  new Promise((resolve, reject) => {
    if (isReady()) {
      resolve();
      return;
    }

    let timer;
    const fail = (error) => {
      clearInterval(timer);
      reject(error);
    };

    const src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = src;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        // Remove it so the next attempt loads it again.
        script.remove();
        fail(new Error(`Failed to load ${src}. Check the network, ad blockers and the Content-Security-Policy (www.google.com, www.gstatic.com).`));
      };
      document.head.appendChild(script);
    }

    const started = Date.now();
    timer = setInterval(() => {
      if (isReady()) {
        clearInterval(timer);
        resolve();
      }
      else if (Date.now() - started > TIMEOUT_MS) {
        fail(new Error(`enterprise.js did not initialise within ${TIMEOUT_MS / 1000}s.`));
      }
    }, 100);
  });

const execute = (siteKey, action) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`execute() did not answer within ${TIMEOUT_MS / 1000}s. Check that this domain is allowed for the key.`)),
      TIMEOUT_MS,
    );
    window.grecaptcha.enterprise.ready(() => {
      Promise.resolve(window.grecaptcha.enterprise.execute(siteKey, { action })).then(
        (token) => {
          clearTimeout(timer);
          resolve(token);
        },
        (error) => {
          clearTimeout(timer);
          reject(error instanceof Error ? error : new Error(String(error)));
        },
      );
    });
  });

const ReCaptchaEnterprise = ({
  siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '',
  action = 'CHATBOT',
  onVerify,
  onError,
  style,
}) => {
  const [status, setStatus] = useState('loading');
  // Latest callbacks, so the one-off check below never calls stale ones.
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);
  onVerifyRef.current = onVerify;
  onErrorRef.current = onError;

  useEffect(() => {
    let active = true;

    const fail = (message, error) => {
      logError(`${LOG} ${message}`, error || '');
      if (!active) return;
      setStatus('error');
      onErrorRef.current && onErrorRef.current(error || new Error(message));
    };

    if (!siteKey) {
      fail('REACT_APP_RECAPTCHA_SITE_KEY was not set when the app was built; reCAPTCHA cannot load.');
      return () => {
        active = false;
      };
    }

    setStatus('loading');
    hideBadge();
    loadEnterprise(siteKey)
      .then(() => execute(siteKey, action))
      .then((token) => {
        if (!active) return;
        if (!token) {
          fail(`execute() returned an empty token for action "${action}". Check that the key is a score-based Enterprise key and that this domain is allowed.`);
          return;
        }
        setStatus('done');
        onVerifyRef.current && onVerifyRef.current(token);
      })
      .catch((error) => fail(error.message, error));

    return () => {
      active = false;
    };
  }, [siteKey, action]);

  if (status === 'error') {
    return (
      <div style={{ ...style, color: '#b91c1c', fontSize: 13 }}>
        The security check could not load. Please refresh the page and try again.
      </div>
    );
  }
  if (status === 'loading') {
    return <div style={{ ...style, color: '#6b7280', fontSize: 13 }}>Running a quick security check…</div>;
  }
  return null;
};

export default ReCaptchaEnterprise;
